import { Column } from '@components/ListV2/components/table/VirtualizedTable.types';
import useMoney from '../../../../../../../hooks/useMoney';
import LangEnum from '../../../../../../../lang/LangEnum';
import { TargetUnit } from '../../../../../../../services/modules/target/target.types';
import { OrderObjectTargetsTestIds } from '../../../../../../../utils/testIds/OrderObject.testIds';
import TargetConversionCell from '../../../../../../target/components/TargetConversionCell/TargetConversionCell';
import TargetRowLabel from '../../../../../../target/components/TargetRowLabel/TargetRowLabel';
import { formatTargetShare, formatTargetValue } from '../../../../../../target/targetValue.utils';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import OrderTargetSummaryTooltip from '../components/OrderTargetSummaryTooltip/OrderTargetSummaryTooltip';
import { ORDER_TARGET_MEASURE_COLUMNS } from '../orderObjectTargetsTab.constants';
import {
  OrderTargetRow,
  OrderTargetRowKind,
  OrderTargetsColumnsParams,
} from '../orderObjectTargetsTab.types';

const PLAN_COLUMN_WIDTH = 286;
const COMPACT_PLAN_COLUMN_WIDTH = 172;
const SHARE_COLUMN_WIDTH = 112;
const VALUE_COLUMN_WIDTH = 170;
const CONVERSION_COLUMN_WIDTH = 122;

const useOrderTargetsColumns = ({
  units,
  isCompact,
  onToggle,
  onRevealSummary,
}: OrderTargetsColumnsParams): Column<OrderTargetRow>[] => {
  const { translate, locale } = useTranslation();
  const { formatMoney } = useMoney();

  const formatMeasure = (row: OrderTargetRow, unit: TargetUnit, value: string | null) =>
    row.unit === unit ? formatTargetValue({ value, unit, locale, formatMoney }) : '';

  const rowClick = (row: OrderTargetRow) =>
    row.canToggle ? { onClick: () => onToggle(row.id) } : {};

  const measureColumns = ORDER_TARGET_MEASURE_COLUMNS.filter((measure) =>
    units.includes(measure.unit),
  ).flatMap((measure): Column<OrderTargetRow>[] => [
    {
      name: measure.plannedName,
      title: measure.plannedTitle,
      width: VALUE_COLUMN_WIDTH,
      render: (row) => ({
        node: formatMeasure(row, measure.unit, row.plannedValue),
        ...rowClick(row),
      }),
    },
    {
      name: measure.attainedName,
      title: measure.attainedTitle,
      width: VALUE_COLUMN_WIDTH,
      render: (row) => ({
        node: formatMeasure(row, measure.unit, row.attainedValue),
        ...rowClick(row),
      }),
    },
  ]);

  return [
    {
      name: 'plan',
      title: LangEnum.GLOBAL_PLAN,
      width: isCompact ? COMPACT_PLAN_COLUMN_WIDTH : PLAN_COLUMN_WIDTH,
      canHide: false,
      fixedHorizontalScroll: true,
      render: (row) => {
        const label = (
          <TargetRowLabel
            label={row.label}
            toggleTestId={OrderObjectTargetsTestIds.TOGGLE_BUTTON}
            toggle={
              row.hasToggleSlot
                ? {
                    isEnabled: row.canToggle,
                    isExpanded: row.isExpanded,
                    onToggle: () => onToggle(row.id),
                  }
                : undefined
            }
          />
        );

        return {
          node:
            row.kind === OrderTargetRowKind.TARGET ? (
              <OrderTargetSummaryTooltip
                summary={row.summary}
                onOpen={() => onRevealSummary(row.targetId)}
              >
                {label}
              </OrderTargetSummaryTooltip>
            ) : (
              label
            ),
          ...rowClick(row),
        };
      },
    },
    {
      name: 'plannedShare',
      title: translate(LangEnum.TARGET_OBJECT_COLUMN_VALUE_WITH_UNIT, { unit: '%' }),
      width: SHARE_COLUMN_WIDTH,
      render: (row) => ({
        node: formatTargetShare({
          value: row.plannedValue,
          plannedTotal: row.plannedTotal,
          locale,
        }),
        ...rowClick(row),
      }),
    },
    ...measureColumns,
    {
      name: 'conversion',
      title: LangEnum.GLOBAL_CONVERSION,
      width: CONVERSION_COLUMN_WIDTH,
      render: (row) => ({
        node: <TargetConversionCell attainment={row.conversion} unit={row.unit} />,
        ...rowClick(row),
      }),
    },
  ];
};

export default useOrderTargetsColumns;
